import { FolderCopyOutlined } from "@mui/icons-material";
import saveAs from "file-saver";
import { FC, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useGetDownloadFeedbackReport } from "../../../../../api/services/bulkCreateProgram/bulkCreateProgramApis";
import {
  MuiBox,
  MuiButtonPrimary,
  MuiButtonTertiary,
  MuiTypography,
} from "../../../../../components";
import PermissionWrapper from "../../../../../components/permissionWrapper/PermissionWrapper";
import {
  MODULE_PERMISSIONS,
  PAGE_URLS,
} from "../../../../../helpers/constants";
import { replaceHashWithValue } from "../../../../../helpers/utils";
import { useSiteCore } from "../../../../../hooks/useSiteCore";
import { useToast } from "../../../../../hooks/useToast";
import { siteCoreContentLoadingSelector } from "../../../../../redux/slices/appState/appStateSelector";
import { toggleLoader } from "../../../../../redux/slices/appState/appStateSlice";
import {
  BulkUploadStatusResponse,
  DownloadParamType,
  DownloadType,
  processedPrograms,
  unprocessedPrograms,
} from "../../../modelsAndSchema/Models";
import { bulkCreateProgramSiteCoreKeys } from "../../../siteCore/siteCoreKeys";
import { BulkProgramStatusPanel } from "../../bulkProgramStatusPanel/BulkProgramStatusPanel";
import { BulkSummaryDetailWrapper } from "./BulkCreateProgramSummary.style";

export interface BulkCreateProgramSummaryProps {
  summaryData: BulkUploadStatusResponse;
  onCreateNewBulkProgram: () => void;
  pageId: string;
  isEditBulkProgram: boolean;
}

export const BulkCreateProgramSummary: FC<BulkCreateProgramSummaryProps> = ({
  summaryData,
  onCreateNewBulkProgram,
  pageId,
  isEditBulkProgram,
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { displayToast } = useToast();
  const siteCoreLoading = useSelector(siteCoreContentLoadingSelector);

  const { useFetchLabel, commonLabels } = useSiteCore();
  const [downloadFileName, setDownloadFileName] = useState("");

  const {
    summaryComponent,
    summaryMessage,
    iAmDoneButton,
    createNewBulkProgramLink,
    bulkEditProgramCTAButton,
  } = bulkCreateProgramSiteCoreKeys;

  const getLabel = useFetchLabel(summaryComponent);
  const {
    data: feedbackSummaryData,
    isPending: feedbackSummaryPending,
    isError: feedbackSummaryError,
    isSuccess: feedbackSummarySuccess,
    mutateAsync: downLoadFeedbackSummary,
  } = useGetDownloadFeedbackReport();

  useEffect(() => {
    if (feedbackSummaryData) {
      if (feedbackSummaryData instanceof Blob) {
        saveAs(feedbackSummaryData, downloadFileName);
        setDownloadFileName("");
      }
    }
  }, [feedbackSummaryData]);

  useEffect(() => {
    dispatch(
      toggleLoader({
        isVisible: feedbackSummaryPending,
      }),
    );
    return () => {
      dispatch(toggleLoader({ isVisible: false }));
    };
  }, [feedbackSummaryPending]);

  useEffect(() => {
    if (feedbackSummaryError) {
      displayToast(commonLabels.someThingWentWrongPleaseTryAgainLater, "error");
      dispatch(toggleLoader({ isVisible: false }));
    }
    if (feedbackSummarySuccess) {
      dispatch(toggleLoader({ isVisible: false }));
    }
  }, [feedbackSummaryError, feedbackSummarySuccess]);

  const onImDoneClick = () => {
    navigate(`${PAGE_URLS.SEARCH_PROGRAM}`);
  };

  const onDownloadClick = (type: DownloadType) => {
    const suffixName =
      type === DownloadParamType.PROCESSED_REPORT
        ? processedPrograms
        : unprocessedPrograms;
    setDownloadFileName(`${summaryData.processName}${suffixName}.xlsx`);

    downLoadFeedbackSummary({
      bulkImportId: summaryData.bulkImportId,
      reportType: type,
    });
  };

  const recordStats = useMemo(
    () => ({
      total: {
        count: summaryData.totalRecords,
        label: getLabel(bulkCreateProgramSiteCoreKeys.totalNumberOfRecords),
      },
      processed: {
        count: summaryData.processedRecords,
        label: getLabel(bulkCreateProgramSiteCoreKeys.recordsProcessed),
      },
      unprocessed: {
        count: summaryData.unProcessRecords,
        label: getLabel(bulkCreateProgramSiteCoreKeys.recordsUnprocessed),
      },
    }),
    [
      summaryData.totalRecords,
      summaryData.processedRecords,
      summaryData.unProcessRecords,
      siteCoreLoading,
    ],
  );

  return (
    <BulkSummaryDetailWrapper>
      <BulkProgramStatusPanel
        recordStats={recordStats}
        processName={summaryData.processName}
        status={summaryData.status}
        onDownload={onDownloadClick}
      />

      {summaryData.unProcessRecords > 0 && (
        <MuiTypography
          variant="h5Bold"
          component={"p"}
          className="unprocessed-label"
          data-testid={`${pageId}-unprocessed-message`}
        >
          {replaceHashWithValue(
            getLabel(summaryMessage),
            String(summaryData.unProcessRecords),
          )}
        </MuiTypography>
      )}
      <MuiBox className="done-button-container">
        <MuiButtonPrimary
          onClick={onImDoneClick}
          data-testid={`${pageId}-done-button`}
        >
          {getLabel(iAmDoneButton)}
        </MuiButtonPrimary>
      </MuiBox>
      <PermissionWrapper
        permission={
          isEditBulkProgram
            ? MODULE_PERMISSIONS.BULK_EDIT
            : MODULE_PERMISSIONS.BULK_CREATE
        }
      >
        <MuiBox className="footer-button">
          <MuiButtonTertiary
            onClick={onCreateNewBulkProgram}
            data-testid={"add-new-bulk-program"}
          >
            <FolderCopyOutlined />
            {isEditBulkProgram
              ? getLabel(bulkEditProgramCTAButton)
              : getLabel(createNewBulkProgramLink)}
          </MuiButtonTertiary>
        </MuiBox>
      </PermissionWrapper>
    </BulkSummaryDetailWrapper>
  );
};
